import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apagar, apagarJson, editarJson, enviarJson, pedirJson } from "./api";
import { esquemaTicket, type EstadoDoTicket, type UrgenciaDoTicket } from "./tickets";

/* Operação de chamados: participantes, ciclo com o cliente (aceitar, reabrir, avaliar), tags,
   ações em lote e visões salvas da fila. Fica à parte de `tickets.ts` para as frentes do
   atendimento não disputarem o mesmo arquivo; as chaves de consulta começam em `["tickets"]`,
   então tudo o que `tickets.ts` invalida (responder, alterar) atualiza estas telas também. */

const CHAVE = ["tickets"] as const;
const chaveDoChamado = (id: string) => [...CHAVE, id] as const;

/* ------------------------------------------------------------- participantes */

const esquemaPessoa = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  email: z.string(),
});

const esquemaParticipante = z.object({
  usuario_id: z.string().uuid(),
  nome: z.string(),
  email: z.string(),
  papel: z.enum(["solicitante", "seguidor"]),
  interno: z.boolean(),
  criado_em: z.string(),
});

const esquemaParticipantes = z.object({
  solicitante: esquemaPessoa.nullable(),
  participantes: z.array(esquemaParticipante),
});

const esquemaCandidato = esquemaPessoa.extend({ interno: z.boolean() });

export type Participante = z.infer<typeof esquemaParticipante>;
export type ParticipantesDoChamado = z.infer<typeof esquemaParticipantes>;
export type CandidatoAParticipante = z.infer<typeof esquemaCandidato>;
export type PapelDoParticipante = Participante["papel"];

export const ROTULO_DO_PAPEL: Record<PapelDoParticipante, string> = {
  solicitante: "Solicitante",
  seguidor: "Acompanha",
};

export function useParticipantes(ticketId: string) {
  return useQuery({
    queryKey: [...chaveDoChamado(ticketId), "participantes"],
    queryFn: ({ signal }) =>
      pedirJson(`/tickets/${encodeURIComponent(ticketId)}/participantes`, esquemaParticipantes, {
        signal,
      }),
    enabled: Boolean(ticketId),
  });
}

export function useCandidatosAParticipante(ticketId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...chaveDoChamado(ticketId), "participantes", "candidatos"],
    queryFn: ({ signal }) =>
      pedirJson(
        `/tickets/${encodeURIComponent(ticketId)}/participantes/candidatos`,
        z.array(esquemaCandidato),
        { signal },
      ),
    enabled: enabled && Boolean(ticketId),
  });
}

export function useAdicionarParticipante(ticketId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: { usuario_id: string; papel: PapelDoParticipante }) =>
      enviarJson(
        `/tickets/${encodeURIComponent(ticketId)}/participantes`,
        esquemaParticipantes,
        corpo,
      ),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaveDoChamado(ticketId) }),
  });
}

export function useRemoverParticipante(ticketId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (usuarioId: string) =>
      apagarJson(
        `/tickets/${encodeURIComponent(ticketId)}/participantes/${encodeURIComponent(usuarioId)}`,
        esquemaParticipantes,
      ),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaveDoChamado(ticketId) }),
  });
}

/* --------------------------------------------------------------------- ciclo */

const esquemaAvaliacao = z.object({
  nota: z.number().int().min(1).max(5),
  comentario: z.string().nullable(),
  avaliado_por: z.string().uuid().nullable(),
  criada_em: z.string(),
});

const esquemaCiclo = z.object({
  estado: esquemaTicket.shape.estado,
  reaberturas: z.number().int().nonnegative(),
  reaberto_em: z.string().nullable(),
  resolvido_em: z.string().nullable(),
  avaliacao: esquemaAvaliacao.nullable(),
});

export type CicloDoChamado = z.infer<typeof esquemaCiclo>;
export type AvaliacaoDoChamado = z.infer<typeof esquemaAvaliacao>;

export function useCiclo(ticketId: string) {
  return useQuery({
    queryKey: [...chaveDoChamado(ticketId), "ciclo"],
    queryFn: ({ signal }) =>
      pedirJson(`/tickets/${encodeURIComponent(ticketId)}/ciclo`, esquemaCiclo, { signal }),
    enabled: Boolean(ticketId),
  });
}

function useAtoDoCiclo<T>(ticketId: string, enviar: (caminho: string, corpo: T) => Promise<unknown>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: T) => enviar(`/tickets/${encodeURIComponent(ticketId)}`, corpo),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaveDoChamado(ticketId) });
      void cliente.invalidateQueries({ queryKey: CHAVE });
    },
  });
}

export function useAceitarSolucao(ticketId: string) {
  return useAtoDoCiclo<void>(ticketId, (base) => enviarJson(`${base}/aceite`, esquemaCiclo, {}));
}

export function useReabrirChamado(ticketId: string) {
  return useAtoDoCiclo<string>(ticketId, (base, motivo) =>
    enviarJson(`${base}/reabertura`, esquemaCiclo, { motivo }),
  );
}

export function useAvaliarAtendimento(ticketId: string) {
  return useAtoDoCiclo<{ nota: number; comentario: string | null }>(ticketId, (base, corpo) =>
    enviarJson(`${base}/avaliacao`, esquemaCiclo, corpo),
  );
}

const esquemaResumoDoCiclo = z.object({
  chamados_reabertos: z.number().int().nonnegative(),
  reaberturas: z.number().int().nonnegative(),
  avaliacoes: z.number().int().nonnegative(),
  nota_media: z.number().nullable(),
  distribuicao: z.record(z.string(), z.number().int().nonnegative()),
});

export type ResumoDoCiclo = z.infer<typeof esquemaResumoDoCiclo>;

export function useResumoDoCiclo(enabled: boolean, organizacaoId = "") {
  return useQuery({
    queryKey: [...CHAVE, "ciclo", "resumo", organizacaoId],
    queryFn: ({ signal }) =>
      pedirJson(
        `/tickets/ciclo/resumo${organizacaoId ? `?organizacao_id=${encodeURIComponent(organizacaoId)}` : ""}`,
        esquemaResumoDoCiclo,
        { signal },
      ),
    enabled,
  });
}

/* ---------------------------------------------------------------------- tags */

const esquemaTagsDoChamado = z.object({ tags: z.array(z.string()) });
const esquemaTagEmUso = z.object({ tag: z.string(), chamados: z.number().int().nonnegative() });

export type TagEmUso = z.infer<typeof esquemaTagEmUso>;

export function useTagsDoChamado(ticketId: string, enabled = true) {
  return useQuery({
    queryKey: [...chaveDoChamado(ticketId), "tags"],
    queryFn: ({ signal }) =>
      pedirJson(`/tickets/${encodeURIComponent(ticketId)}/tags`, esquemaTagsDoChamado, { signal }),
    enabled: enabled && Boolean(ticketId),
  });
}

export function useTagsEmUso(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "tags"],
    queryFn: ({ signal }) => pedirJson("/tickets/tags", z.array(esquemaTagEmUso), { signal }),
    enabled,
  });
}

function useMudancaDeTag<T>(
  ticketId: string,
  enviar: (base: string, valor: T) => Promise<unknown>,
) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (valor: T) => enviar(`/tickets/${encodeURIComponent(ticketId)}`, valor),
    onSuccess: () => {
      // A fila filtra por tag e mostra o catálogo de tags: tudo em `["tickets"]` se refaz.
      void cliente.invalidateQueries({ queryKey: CHAVE });
    },
  });
}

export function useAdicionarTag(ticketId: string) {
  return useMudancaDeTag<string>(ticketId, (base, tag) =>
    enviarJson(`${base}/tags`, esquemaTagsDoChamado, { tag }),
  );
}

export function useRemoverTag(ticketId: string) {
  return useMudancaDeTag<string>(ticketId, (base, tag) =>
    apagarJson(`${base}/tags/${encodeURIComponent(tag)}`, esquemaTagsDoChamado),
  );
}

/* --------------------------------------------------------------------- lote */

const esquemaResultadoDoLote = z.object({
  lote_id: z.string().uuid(),
  aplicado: z.boolean(),
  total: z.number().int().nonnegative(),
  alterados: z.number().int().nonnegative(),
  sem_mudanca: z.number().int().nonnegative(),
  falhas: z.array(
    z.object({
      ticket_id: z.string().uuid(),
      numero: z.number().int().nullable(),
      codigo: z.string(),
      motivo: z.string(),
    }),
  ),
});

export type ResultadoDoLote = z.infer<typeof esquemaResultadoDoLote>;

/** Só o que estiver presente muda; `null` explícito retira responsável, equipe, categoria ou serviço. */
export interface AlteracoesDoLote {
  estado?: EstadoDoTicket;
  responsavel_id?: string | null;
  equipe_id?: string | null;
  categoria?: string | null;
  servico?: string | null;
}

export function useAlterarEmLote() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: { ticket_ids: string[]; alteracoes: AlteracoesDoLote }) =>
      enviarJson("/tickets/lote", esquemaResultadoDoLote, corpo),
    onSuccess: (resultado) => {
      if (resultado.aplicado) void cliente.invalidateQueries({ queryKey: CHAVE });
    },
  });
}

/* ------------------------------------------------------------------- visões */

const esquemaFiltrosDaVisao = z.object({
  texto: z.string().optional(),
  estado: esquemaTicket.shape.estado.optional(),
  urgencia: esquemaTicket.shape.urgencia.optional(),
  equipe_id: z.string().uuid().optional(),
  responsavel_id: z.string().uuid().optional(),
  categoria: z.string().optional(),
  tag: z.string().optional(),
  organizacao_id: z.string().uuid().optional(),
});

const esquemaVisao = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  modo: z.enum(["lista", "kanban"]),
  filtros: esquemaFiltrosDaVisao,
  compartilhada: z.boolean(),
  dono_id: z.string().uuid(),
  dono_nome: z.string().nullable(),
  minha: z.boolean(),
  atualizada_em: z.string(),
});

export type VisaoDaFila = z.infer<typeof esquemaVisao>;
export type FiltrosDaVisao = z.infer<typeof esquemaFiltrosDaVisao>;
export type ModoDaFila = VisaoDaFila["modo"];

/** Os filtros da fila como a visão os guarda: sem chave vazia. */
export function filtrosParaSalvar(filtros: {
  texto?: string;
  estado?: EstadoDoTicket | "";
  urgencia?: UrgenciaDoTicket | "";
  equipe_id?: string;
  responsavel_id?: string;
  tag?: string;
}): FiltrosDaVisao {
  const resultado: Record<string, string> = {};
  for (const [chave, valor] of Object.entries(filtros)) {
    const limpo = typeof valor === "string" ? valor.trim() : "";
    if (limpo) resultado[chave] = limpo;
  }
  return resultado as FiltrosDaVisao;
}

export function useVisoes(enabled = true) {
  return useQuery({
    queryKey: [...CHAVE, "visoes"],
    queryFn: ({ signal }) => pedirJson("/tickets/visoes", z.array(esquemaVisao), { signal }),
    enabled,
  });
}

function useMudancaDeVisao<T, R = unknown>(enviar: (corpo: T) => Promise<R>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: enviar,
    onSuccess: () => void cliente.invalidateQueries({ queryKey: [...CHAVE, "visoes"] }),
  });
}

export function useSalvarVisao() {
  return useMudancaDeVisao<
    { nome: string; modo: ModoDaFila; filtros: FiltrosDaVisao; compartilhada: boolean },
    VisaoDaFila
  >((corpo) => enviarJson("/tickets/visoes", esquemaVisao, corpo));
}

export function useAlterarVisao() {
  return useMudancaDeVisao<{
    id: string;
    campos: { nome?: string; modo?: ModoDaFila; filtros?: FiltrosDaVisao; compartilhada?: boolean };
  }>(({ id, campos }) => editarJson(`/tickets/visoes/${encodeURIComponent(id)}`, esquemaVisao, campos));
}

export function useApagarVisao() {
  return useMudancaDeVisao<string>((id) => apagar(`/tickets/visoes/${encodeURIComponent(id)}`));
}
